import { useEffect, useState } from "react";
import { Loader2, Trash2, Heart, Lightbulb, HelpCircle, Footprints } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { useSubjects } from "@/lib/buddy";
import { VoiceInput } from "@/components/buddy/VoiceInput";

const BINS = [
  ["feelings", "Feelings", Heart, "#E8B4B8"], ["ideas", "Ideas", Lightbulb, "#F2D49B"],
  ["questions", "Questions", HelpCircle, "#BFD4E6"], ["next_steps", "Next steps", Footprints, "#C8DCCB"],
];

export const SortResult = ({ s, onDelete }) => (
  <article className="rounded-3xl bg-white/70 border border-[#E6DEC3] p-6 rise" data-testid={`sort-${s.id}`}>
    <div className="flex justify-between gap-3">
      <p className="text-sm text-stone-500 italic line-clamp-2">"{s.text}"</p>
      {onDelete && <button onClick={() => onDelete(s.id)} data-testid={`sort-delete-${s.id}`} className="text-stone-400 hover:text-red-500" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>}
    </div>
    <div className="grid sm:grid-cols-2 gap-3 mt-4">
      {BINS.map(([k, label, Icon, color]) => (
        <div key={k} className="rounded-2xl p-4" style={{ background: `${color}55` }} data-testid={`sort-bin-${k}`}>
          <p className="text-xs uppercase tracking-widest text-[#2C4A3E] font-semibold inline-flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" />{label}</p>
          <ul className="mt-2 space-y-1 text-sm text-stone-700">{(s[k] || []).map((x, i) => <li key={i}>• {x}</li>)}{!s[k]?.length && <li className="text-stone-400">—</li>}</ul>
        </div>
      ))}
    </div>
    {s.buddy_note && <p className="mt-4 font-display text-[#2C4A3E] leading-relaxed" data-testid="sort-buddy-note">{s.buddy_note}</p>}
  </article>
);

export const ThoughtSorter = () => {
  const { buddy } = useProfile();
  const subjects = useSubjects();
  const [text, setText] = useState("");
  const [subject, setSubject] = useState("");
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState([]);
  useEffect(() => { http.get("/buddy/sorts").then((r) => setList(r.data)).catch(() => {}); }, []);

  const sort = async () => {
    if (text.trim().length < 3) return;
    setBusy(true);
    try {
      const r = await http.post("/buddy/sort", { text, subject_id: subject || null });
      setList((l) => [r.data, ...l]); setText("");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const remove = async (id) => { await http.delete(`/buddy/sorts/${id}`); setList((l) => l.filter((x) => x.id !== id)); };

  return (
    <div data-testid="thought-sorter">
      <div className="rounded-3xl bg-[#F7F3EB] border border-[#E6DEC3] p-6">
        <p className="font-display text-xl text-[#2C4A3E]">Brain-dump everything. {buddy.name} will help sort it.</p>
        <p className="text-sm text-stone-500 mt-1">Messy is perfect. Type or tap the mic and just talk.</p>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} maxLength={4000} data-testid="sorter-input"
          placeholder="e.g. I have a science test and I don't get photosynthesis, also I'm kinda stressed about my friend…"
          className="w-full mt-4 px-4 py-3 rounded-2xl bg-white border border-[#E6DEC3] focus:outline-none focus:border-[#2C4A3E] text-sm" />
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <VoiceInput onText={(t) => setText((x) => (x ? `${x} ${t}` : t))} />
          <select value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="sorter-subject-select" className="px-3 py-2 rounded-full bg-white border border-[#E6DEC3] text-sm">
            <option value="">Any subject</option>
            {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button onClick={sort} disabled={busy || text.trim().length < 3} data-testid="sorter-submit-btn"
            className="ml-auto inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#2C4A3E] text-white disabled:opacity-50 hover:bg-[#1E352B] transition-colors">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {busy ? `${buddy.name} is sorting…` : "Sort my thoughts"}
          </button>
        </div>
      </div>
      <div className="space-y-5 mt-8" data-testid="sorter-history">
        {list.map((s) => <SortResult key={s.id} s={s} onDelete={remove} />)}
        {!list.length && <p className="text-stone-500 text-sm" data-testid="sorter-empty">No sorted thoughts yet.</p>}
      </div>
    </div>
  );
};
